import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {connect} from 'react-redux'
import Dialog from "components/Dialog";
import Tr from "translations/Translations";
import DoubleButton from "components/popup/components/DoubleButton";
import Typography from "../../Typography";
import {makeStyles} from "@material-ui/styles";
import BoldTypography from "components/BoldTypography";
import EzamowieniaService from "services/EzamowieniaService";
import reduxStore from "redux/store";
import {clearEzamowieniaOauth, setEzamowieniaOauth} from "redux/actions";

const useStyle = makeStyles(theme => ({
    marginTop: {
        marginTop: theme.spacing()
    }
}))


function EzamowieniaOauth({ezamowieniaOauth}) {
    const classes = useStyle();

    const [confirmationWindow, setConfirmationWindow] = useState(null);
    const authWindowInterval = useRef(null);
    const authWindow = useRef(null);
    const ezamowieniaOauthRef = useRef(null);
    const callbackUrlRef = useRef(null);

    const closeWindow = () => {
        if(authWindowInterval.current)
            clearInterval(authWindowInterval.current);
        authWindowInterval.current = null;
        if(authWindow.current) {
            try {
                authWindow.current.close();
            } catch (e) {
                console.error(e)
            }
            authWindow.current = null;
        }
    }

    const openWindow = useCallback((url) => {
        closeWindow();
        authWindow.current = window.open(url, '_blank', 'location=no,height=570,width=520,scrollbars=yes,status=no');
        if(authWindow.current) {
            authWindowInterval.current = setInterval(() => {
                if(authWindowInterval.current && (!authWindow.current || authWindow.current.closed)) {
                    clearInterval(authWindowInterval.current);
                    authWindowInterval.current = null;
                    callFail();
                }
            }, 1000);
        }
    },[])

    useEffect(() => {
        if (ezamowieniaOauth) {
            ezamowieniaOauthRef.current = ezamowieniaOauth;

            EzamowieniaService.getAuthorizationStatus().then((resp) => {
                if (resp) {
                    const {authorized, authorizationUrl, logoutUrl, userInfo, callbackOrigin} = resp;
                    callbackUrlRef.current = callbackOrigin;
                    if (!authorized) {
                        openWindow(authorizationUrl);
                    } else {
                        setConfirmationWindow({userInfo, logoutUrl, requiredGroups: ezamowieniaOauth.requiredGroups})
                    }
                } else {
                    if (ezamowieniaOauth.errorCallback) ezamowieniaOauth.errorCallback();
                }
            });
        } else {
            setConfirmationWindow(null);
            authWindow.current = null;
            ezamowieniaOauthRef.current = null;
            authWindowInterval.current = null;
            callbackUrlRef.current = null;
        }
    }, [ezamowieniaOauth, openWindow])


    useEffect(() => {
        window.addEventListener("message", receiveMessage, false);
        return () => {
            window.removeEventListener("message", receiveMessage)
        };
        // eslint-disable-next-line
    }, [])

    const receiveMessage = (event) => {
        if (event.origin === callbackUrlRef.current) {
            if (event.data.action && (typeof event.data.action === 'string' || event.data.action instanceof String)) {
                if (event.data.action === "ezamowieniaAuthorizationSuccess") {
                    closeWindow();
                    if(ezamowieniaOauthRef.current) {
                        reduxStore.dispatch(setEzamowieniaOauth({...ezamowieniaOauthRef.current}))
                    }
                }
                if (event.data.action === "ezamowieniaAuthorizationFailed") {
                    callFail();
                }
                if (event.data.action === "ezamowieniaAuthorizationLogout") {
                    closeWindow();
                    callFail();
                    reduxStore.dispatch(clearEzamowieniaOauth());
                }
            }
        }
    }

    const callSuccess = () => {
        if (ezamowieniaOauthRef.current && ezamowieniaOauthRef.current.successCallback) {
            ezamowieniaOauthRef.current.successCallback();
        }
        reduxStore.dispatch(clearEzamowieniaOauth())
    }

    const callFail = () => {
        if (ezamowieniaOauthRef.current && ezamowieniaOauthRef.current.errorCallback) {
            ezamowieniaOauthRef.current.errorCallback();
        }
        reduxStore.dispatch(clearEzamowieniaOauth())
    }

    const callLogout = () => {
        EzamowieniaService.revokeToken();
        if(confirmationWindow && confirmationWindow.logoutUrl) {
            openWindow(confirmationWindow.logoutUrl);
        }
        setConfirmationWindow(null);
    }

    const missingRoles = useMemo(() => {
        if (!!confirmationWindow?.requiredGroups?.length > 0) {
            return confirmationWindow.requiredGroups.filter(x => !Array.isArray(confirmationWindow.userInfo.groups) || !confirmationWindow.userInfo.groups.includes(x));
        }
        return [];
    }, [confirmationWindow]);

    const continueEnabled = useMemo(()=> {
        if (!!confirmationWindow?.userInfo?.organization_role?.includes("BUYER") && missingRoles.length === 0) {
            return true;
        }
        return false

    }, [confirmationWindow, missingRoles])


    return !!confirmationWindow &&
        <Dialog
            title={Tr('ezamowienia_confirm_title')}
            open
            maxWidth="md"
            onClose={callFail}
            dialogActions={<DoubleButton
                justify='flex-end'
                labelFirst={ Tr(continueEnabled ? "ezamowienia_confirm_use_other_button" : "ezamowienia_confirm_logout_button")}
                labelSecond={Tr('ezamowienia_confirm_continue_button')}
                onClickFirst={callLogout}
                onClickSecond={callSuccess}
                disabledSecond={!continueEnabled}
            />}>
            <Typography component="span">{Tr("ezamowienia_loggedin_user")}: <strong>{
                !!confirmationWindow.userInfo.given_name ?
                    `${confirmationWindow.userInfo.given_name} ${confirmationWindow.userInfo.family_name} (${confirmationWindow.userInfo.sub})` :
                    confirmationWindow.userInfo.sub
            }</strong></Typography>

            <> {
                !confirmationWindow.userInfo.organizationName ?
                    <Typography component="span" color="error" className={classes.marginTop}>{Tr("ezamowienia_loggedin_user_no_org_account")}</Typography>
                    :
                    <>
                        <Typography
                            component="span">{Tr("ezamowienia_loggedin_organization")}: <strong>{confirmationWindow.userInfo.organizationName}</strong></Typography>
                        {
                            !confirmationWindow.userInfo.organization_role?.includes("BUYER") ?
                                <Typography color="error" className={classes.marginTop}>{Tr("ezamowienia_loggedin_missing_buyer_org_role")}</Typography>
                                :
                                <>
                                    {
                                        missingRoles.length > 0 && <>
                                            <Typography color="error" className={classes.marginTop}>{Tr("ezamowienia_loggedin_user_missing_org_roles")}</Typography>
                                            {missingRoles.map(x => <BoldTypography color="error">• {Tr("ezamowienia_auth_group_" + x)}</BoldTypography>)}
                                        </>

                                    }
                                </>
                        }

                    </>
            }
            </>


        </Dialog>;
}

export default connect((state) => ({
    ezamowieniaOauth: state.store.ezamowieniaOauth
}), {})(EzamowieniaOauth)